import { useState, useEffect, useRef } from 'react';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';
import { observeProfile, type ProfileState } from '../services/profileSession';

// Types pour le profil
export interface UserProfile {
  id: string;
  spotifyId: string | null;
  displayName: string;
  avatarUrl: string | null;
  isGuest: boolean;
  isPremium: boolean;
  level: number;
  xp: number;
  
  // Stats
  totalScore: number;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
  titlesFound: number;
  artistsFound: number;
  perfectGuesses: number;
  currentStreak: number;
  bestStreak: number;
  bestCombo: number;
  avgResponseTime: number;
  
  // Ranks
  rankScore: number;
  rankWins: number;
  rankTitles: number;
  
  // Badges
  badges: UserBadge[];
  
  // Recent games
  recentGames: GameHistory[];
  
  // Dates
  createdAt: string;
  lastLogin: string | null;
}

export interface UserBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  earnedAt: string;
}

export interface GameHistory {
  id: string;
  roomName: string;
  gameMode: string;
  theme: string | null;
  finalScore: number;
  rank: number;
  titlesFound: number;
  perfectGuesses: number;
  playerCount: number;
  finishedAt: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  requirementType: string;
  requirementValue: number;
  xpReward: number;
}

export function useProfile() {
  const { socket, isConnected, isSocketAuthenticated, sessionError } = useSocketStore();
  const { isAuthenticated, player } = useAuthStore();
  const eligible = isConnected && isSocketAuthenticated && isAuthenticated && player?.type !== 'guest';
  const [state, setState] = useState<ProfileState>({ profile: null, isLoading: false, error: null });
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const session = useRef<ReturnType<typeof observeProfile> | null>(null);
  const fetchProfile = () => {
    if (session.current) session.current.fetchProfile();
    else if (isAuthenticated && player?.type !== 'guest') {
      useSocketStore.getState().disconnect(); useSocketStore.getState().connect();
    }
  };
  const fetchAllBadges = () => { if (socket && eligible) socket.emit('badges:getAll'); };
  useEffect(() => {
    setState({ profile: null, isLoading: false, error: null }); setAllBadges([]);
    if (!socket || !eligible || !player) return;
    session.current = observeProfile(socket, player.id, setState);
    const receiveBadges = ({ badges }: { badges: Badge[] }) => setAllBadges(badges);
    socket.on('badges:list', receiveBadges); socket.emit('badges:getAll');
    return () => { session.current?.dispose(); session.current = null; socket.off('badges:list', receiveBadges); };
  }, [socket, eligible, player?.id]);
  return { ...state, error: eligible ? state.error : sessionError, profile: eligible ? state.profile : null, allBadges: eligible ? allBadges : [], fetchProfile, fetchAllBadges };
}

// Helper pour formater le temps de réponse
export function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Helper pour obtenir la couleur de rareté
export function getRarityColor(rarity: string): string {
  switch (rarity) {
    case 'common': return 'text-gray-400';
    case 'uncommon': return 'text-green-400';
    case 'rare': return 'text-blue-400';
    case 'epic': return 'text-purple-400';
    case 'legendary': return 'text-yellow-400';
    default: return 'text-gray-400';
  }
}

// Helper pour obtenir le bg de rareté
export function getRarityBg(rarity: string): string {
  switch (rarity) {
    case 'common': return 'bg-gray-500/20 border-gray-500/30';
    case 'uncommon': return 'bg-green-500/20 border-green-500/30';
    case 'rare': return 'bg-blue-500/20 border-blue-500/30';
    case 'epic': return 'bg-purple-500/20 border-purple-500/30';
    case 'legendary': return 'bg-yellow-500/20 border-yellow-500/30 animate-pulse';
    default: return 'bg-gray-500/20';
  }
}
